import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ClaimFileDto } from "../../types";
import { formatDocumentOnlyDate, formatDocumentTime } from "../../../../utils/formatters/DateFormatter";
import { useEffect, useState } from "react";
import { summaryClaimFile, updateClaimFile } from "../../api/ClaimFileAPI";
import { useAlert } from "../../../../hooks/useAlert";
import AlertMessage from "../../../../components/AlertMessage";

interface ClaimFileDetailsPanelProps {
    file: ClaimFileDto | null;
    onBack: () => void;
    onSaved?: (file: ClaimFileDto) => void;
}

export default function ClaimFileDetailsPanel({ file, onBack, onSaved }: ClaimFileDetailsPanelProps) {
    const { t } = useTranslation();
    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });
    const [fileClaimDto, setClaimFileDto] = useState<ClaimFileDto | null>(file);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setClaimFileDto(file);
    }, [file]);

    if (!file) return null;

    const changeInputField = (field: keyof ClaimFileDto) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setClaimFileDto((prev) => ({
            ...(prev ?? file),
            [field]: e.target.value
        }));
    };

    const onSave = async () => {
        try {
            const currentFile = fileClaimDto ?? file;
            if (!currentFile?.id) return;

            await updateClaimFile(String(currentFile.id), currentFile);
            onSaved?.(currentFile);
            onBack();
        } catch {
            showAlert({
                message: t("common:errors.networkError"),
                type: "danger",
                duration: 3000,
            });
        }
    };

    const onSummary = async () => {
        setIsLoading(true);
        try {
            const summaryResponse = await summaryClaimFile(String(file.id ?? "0"));
            setClaimFileDto((prev) => ({
                ...(prev ?? file),
                summary: summaryResponse.text?.replace(/\n+/g, " ").trim() ?? "",
            }));
        } catch {
            showAlert({
                message: t("common:errors.networkError"),
                type: "danger",
                duration: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <Card className="h-100">
            <Card.Body className="d-flex flex-column">
                {isVisible && <AlertMessage message={alert!.message} type={alert!.type} onClose={hideAlert} />}
                <Row className="g-2">
                    <Col md={12}>
                        <Form.Label>{t("claim:detailsPanel.claimFileName")}</Form.Label>
                        <Form.Control readOnly value={file.fileName ?? ""} />
                    </Col>
                    <Col md={12}>
                        <Form.Label>{t("claim:detailsPanel.createdBy")}</Form.Label>
                        <Form.Control readOnly value={file.createdUserFullName ?? ""} />
                    </Col>
                    <Col md={6}>
                        <Form.Label>{t("claim:detailsPanel.createdDate")}</Form.Label>
                        <Form.Control readOnly value={formatDocumentOnlyDate(file.createdDate)} />
                    </Col>
                    <Col md={6}>
                        <Form.Label>{t("claim:detailsPanel.createdDateTime")}</Form.Label>
                        <Form.Control readOnly value={formatDocumentTime(file.createdDate)} />
                    </Col>
                    <Col md={12}>
                        <Form.Label>{t("claim:detailsPanel.summaryFile")}</Form.Label>
                        <Form.Control as="textarea" rows={3} value={fileClaimDto?.summary ?? ""} onChange={changeInputField('summary')} />
                    </Col>
                </Row>
                <div className="d-flex justify-content-end mt-3">
                    <Col xs="auto" md="auto" className="text-end d-flex justify-content-end">
                        <Button variant="secondary" className="text-nowrap" onClick={onSummary} disabled={isLoading}>
                            {isLoading ? t("common:buttons.buttonLoadingProgress") : t("claim:detailsPanel.buttonSummary")}
                        </Button>
                    </Col>
                </div>
                <div className="d-flex justify-content-center mt-auto pt-3">
                    <Col xs="auto" md="auto" className="text-end d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onBack}>
                            {t("common:buttons.buttonBack")}
                        </Button>
                        <Button variant="primary" onClick={onSave} disabled={!fileClaimDto?.id}>
                            {t("common:buttons.buttonSave")}
                        </Button>
                    </Col>
                </div>
            </Card.Body>
        </Card>
    );
}
